<template>
  <div
    :class="{ 'full-height': fullHeight, 'full-width': fullWidth }"
  >
    <q-btn-dropdown
      v-if="$q.platform.is.capacitor && $q.platform.is.android"
      ref="dropdown"
      v-ripple
      :flat="flat"
      :rounded="round"
      :label="label"
      :size="size"
      :color="color"
      :icon="icon"
      :stack="stack"
      class="without-icon"
      :class="{ 'full-height': fullHeight, 'full-width': fullWidth }"
      padding="9px"
      :disable="disable"
    >
      <q-list>
        <q-item
          v-for="(input, idx) in capacitorInputs"
          :key="idx"
          clickable
        >
          <input
            ref="file"
            type="file"
            :accept="input.accept"
            :capture="input.capture"
            class="file-overlay cursor-pointer attachment-input"
            :disabled="disable"
            @input="onUploadFiles"
          >
          <q-item-section
            avatar
            class="q-pr-sm media-section"
          >
            <q-icon
              :name="input.icon"
              color="primary"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-capitalize">
              {{ $t(input.label) }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>

      <template
        v-if="hasDefaultSlot"
        #label
      >
        <slot />
      </template>
    </q-btn-dropdown>
    <q-btn
      v-else
      v-ripple
      :flat="flat"
      :round="round"
      :label="label"
      :size="size"
      :color="color"
      :icon="icon"
      :stack="stack"
      class="cursor-pointer"
      :class="{ 'full-height': fullHeight, 'full-width': fullWidth }"
      :disable="disable"
    >
      <input
        ref="file"
        type="file"
        :accept="accept"
        class="file-overlay cursor-pointer attachment-input"
        :disabled="disable"
        @input="onUploadFiles"
      >
      <slot />
    </q-btn>
  </div>
</template>
<script>
import SessionUploadWarning from 'components/Modals/SessionUploadWarning';
import { Timestamp } from 'firebase/firestore';
import { get, isNil, map } from 'lodash';
import { mapActions, mapState } from 'pinia';
import SessionFilesDB from 'src/fb/sessionFiles-db';
import Activities from 'src/mixins/Activities';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { firebaseUploader } from 'src/store/firebaseUploader';

export default {
  name: 'FileAttachment',

  mixins: [Toolbox, Activities, Interactions],

  props: {
    filePath: {
      type: String,
      required: true,
    },

    /** Session to upload file to. Can be left to null if in Lazy mode */
    session: {
      type: Object,
      default: null,
    },

    sessionProgram: {
      type: String,
      default: null,
    },

    icon: {
      type: String,
      default: 'video_library',
    },

    size: {
      type: String,
      default: 'sm',
    },

    flat: {
      type: Boolean,
      default: false,
    },

    color: {
      type: String,
      default: 'primary',
    },

    accept: {
      type: String,
      default: '*',
    },
    round: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    stack: {
      type: Boolean,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    showWarning: {
      type: Boolean,
      default: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click', 'select', 'upload-complete'],

  data() {
    return {
      files: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),

    mediaType() {
      let mediaType = 'media';

      if (this.accept.includes('video/')) {
        mediaType = 'video';
      } else if (this.accept.includes('image/')) {
        mediaType = 'image';
      }

      return mediaType;
    },

    capacitorInputs() {
      const options = {
        LIBRARY: {
          label: 'fileAttachment.library',
          icon: 'folder',
          accept: this.accept,
        },
        IMAGE: {
          label: 'fileAttachment.takePhoto',
          icon: 'photo_camera',
          capture: 'environment',
          accept: this.accept,
        },
        VIDEO: {
          label: 'fileAttachment.takeVideo',
          icon: 'videocam',
          capture: 'environment',
          accept: this.accept,
        },
      };

      const inputs = [options.LIBRARY];

      switch (this.mediaType) {
        case 'media':
          inputs.push({
            ...options.IMAGE,
            accept: 'image/*',
          }, {
            ...options.VIDEO,
            accept: 'video/*',
          });
          break;
        case 'image':
          inputs.push(options.IMAGE);
          break;
        case 'video':
          inputs.push(options.VIDEO);
          break;
        default: break;
      }

      return inputs;
    },

    hasDefaultSlot() {
      return !isNil(get(this.$slots, 'default'));
    },
  },

  methods: {
    ...mapActions(firebaseUploader, ['addToUploadQueue']),

    async submit(session) {
      if (this.files === null) {
        return;
      }
      const uploadActions = map(this.files, (file) => this.uploadFile(file, session));
      await Promise.all(uploadActions);
    }, // submit()

    onUploadFiles(e) {
      if (this.$refs && this.$refs.dropdown) {
        this.$refs.dropdown.hide();
      }

      this.files = e.target.files || e.dataTransfer.files;
      const { invalidExtMsg } = this.imageExtensionValidation(get(this.files, 0, {}));

      if (invalidExtMsg) {
        this.notifyWarning(invalidExtMsg);
        return;
      }

      this.$emit('click', this.files);
      if (!this.lazy) {
        this.submit(this.session);
      }
    }, // onUploadFiles()

    async uploadFile(file, session) {
      const event = 'sessionFilesAttached';

      const filename = `${new Date().getTime()}_${file.name}`;
      const client = session?.client || this.currentClient?.id || null;
      const clinic = session?.clinic || this.currentClient?.clinic || null;

      const uploadedFileInfo = {
        originalFilename: filename,
        type: file.type,
        size: file.size,
        recordEndTimestamp: Timestamp.now(),
        uploadedBy: get(this.userDetails, 'userId', null),
        client,
        clinic,
        session: get(session, 'id', null),
        sessionProgram: this.sessionProgram || null,
        status: 'uploading',
      };
      this.$emit('select', uploadedFileInfo);

      // Save info about session and current client in case this upload takes a while
      const activityInfo = {
        originalSession: session.originalSessionId || session.id,
        session: session.id,
        client,
      };

      const sessionFilesDb = new SessionFilesDB(session.id);
      const { id } = await sessionFilesDb.create(uploadedFileInfo);

      const uploadInfo = {
        destinationPath: this.filePath,
        destinationFilename: filename,
        file,
        metadata: {
          customMetadata: {
            user: get(this.userDetails, 'userId', null),
            client,
            clinic,
            originalSession: session.originalSessionId || session.id,
            alias: get(this.currentClient, 'alias', ''),
            addActivity: !this.eventsFired.includes(event),
            event,
            sessionFile: id,
            fileCategory: 'session',
          },
        },
        callback: async () => {
          // file data is saved to firestore in finalizeSessionFile() cloud function
          this.$emit('upload-complete', uploadedFileInfo);

          if (this.$refs.file) {
            this.$refs.file.value = '';
          }
        },
      };

      if (uploadedFileInfo.session) {
        this.addActivity(event, true, null, activityInfo, false);
      }

      if (this.showWarning) {
        const dontShowUploadingWarning = localStorage.getItem('dont_show_uploading_warning');

        if (!dontShowUploadingWarning) {
          this.openComponentDialog(SessionUploadWarning);
        }
      }

      return this.addToUploadQueue(uploadInfo);
    },

  },

};
</script>

<style>
.without-icon .q-btn-dropdown__arrow {
  display: none;
}

.media-section {
  min-width: unset;
}

.attachment-input:disabled {
  opacity: 0 !important;
}
</style>
